/**
 * P4 储备公海管理（admin / director / manager）
 *   - 单个录入：phone + name + source_notes
 *   - 批量上传：粘贴或文本框，每行一个 phone
 */
import { useState } from 'react';
import { Modal, Input, message, Tabs } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { PageHeader, HomeCard, Btn, Pill, TableWrap } from '../../components/ui';
import { fmtDateTime } from '../../lib/format';

export function ReservePoolPage() {
  const qc = useQueryClient();
  const [showSingle, setShowSingle] = useState(false);
  const [showBatch, setShowBatch] = useState(false);
  const [tab, setTab] = useState('available');

  const { data: rows } = useQuery<any[]>({
    queryKey: ['reserve-pool', tab],
    queryFn: () => $.get(`/reserve-pool?status=${tab}`),
  });

  return (
    <>
      <PageHeader
        breadcrumb={['管理', '储备公海']}
        title="🐟 储备公海"
        subtitle="客资沉淀池 · 销售员认领后客户进入 L0 加微信阶段"
        actions={
          <>
            <Btn kind="default" onClick={() => setShowBatch(true)}>📂 批量录入</Btn>
            <Btn kind="primary" onClick={() => setShowSingle(true)}>+ 单个录入</Btn>
          </>
        }
      />
      <div className="work-content">
        <Tabs activeKey={tab} onChange={setTab}
              items={[
                { key: 'available', label: `待认领（${rows?.length ?? 0}）` },
                { key: 'claimed', label: '已认领' },
                { key: 'converted', label: '已转客户' },
              ]} />
        <HomeCard title={`列表`}>
          <TableWrap>
            <table className="ck-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>手机</th><th>姓名</th><th>微信</th><th>来源备注</th><th>录入时间</th><th>状态</th>
                </tr>
              </thead>
              <tbody>
                {(rows ?? []).length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)' }}>暂无</td></tr>
                )}
                {(rows ?? []).map((r: any) => (
                  <tr key={r.id}>
                    <td><span style={{ fontFamily: 'monospace' }}>{r.phone ?? '—'}</span></td>
                    <td>{r.name ?? '—'}</td>
                    <td>{r.wechat ?? '—'}</td>
                    <td style={{ maxWidth: 240, whiteSpace: 'normal' }}>{r.sourceNotes ?? '—'}</td>
                    <td>{fmtDateTime(r.uploadedAt)}</td>
                    <td>
                      <Pill tone={r.status === 'available' ? 'success' : r.status === 'claimed' ? 'warning' : 'default'}>
                        {r.status}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </HomeCard>
      </div>

      <SingleModal open={showSingle} onClose={() => setShowSingle(false)} onDone={() => {
        setShowSingle(false);
        qc.invalidateQueries({ queryKey: ['reserve-pool'] });
      }} />
      <BatchModal open={showBatch} onClose={() => setShowBatch(false)} onDone={() => {
        setShowBatch(false);
        qc.invalidateQueries({ queryKey: ['reserve-pool'] });
      }} />
    </>
  );
}

function SingleModal({ open, onClose, onDone }: any) {
  const [form, setForm] = useState({ phone: '', name: '', wechat: '', source_notes: '', demand_type: '' });
  const submit = useMutation({
    mutationFn: () => $.post('/reserve-pool/single', form),
    onSuccess: () => { message.success('已加入储备池'); onDone(); },
    onError: (e: any) => message.error(e?.message ?? '失败'),
  });
  return (
    <Modal title="单个录入"
           open={open} onCancel={onClose}
           footer={
             <>
               <Btn kind="default" onClick={onClose}>取消</Btn>
               <Btn kind="primary" onClick={() => submit.mutate()}
                    disabled={!form.phone || submit.isPending}>
                 {submit.isPending ? '保存中…' : '保存'}
               </Btn>
             </>
           }>
      <Row label="手机（必填，唯一）">
        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </Row>
      <Row label="姓名"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Row>
      <Row label="微信"><Input value={form.wechat} onChange={(e) => setForm({ ...form, wechat: e.target.value })} /></Row>
      <Row label="来源备注"><Input.TextArea value={form.source_notes} rows={2} onChange={(e) => setForm({ ...form, source_notes: e.target.value })} /></Row>
      <Row label="需求类型"><Input value={form.demand_type} onChange={(e) => setForm({ ...form, demand_type: e.target.value })} /></Row>
    </Modal>
  );
}

function BatchModal({ open, onClose, onDone }: any) {
  const [text, setText] = useState('');
  const submit = useMutation({
    mutationFn: () => {
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      const rows = lines.map((phone) => ({ phone }));
      return $.post('/reserve-pool/batch-upload', { rows });
    },
    onSuccess: (r: any) => { message.success(`录入 ${r.created} 条，跳过 ${r.skipped}`); onDone(); },
    onError: (e: any) => message.error(e?.message ?? '失败'),
  });
  return (
    <Modal title="批量录入手机号"
           open={open} onCancel={onClose}
           footer={
             <>
               <Btn kind="default" onClick={onClose}>取消</Btn>
               <Btn kind="primary" onClick={() => submit.mutate()}
                    disabled={!text.trim() || submit.isPending}>
                 {submit.isPending ? '提交中…' : '批量录入'}
               </Btn>
             </>
           }>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>
        每行一个手机号，已存在的自动跳过：
      </div>
      <Input.TextArea value={text} onChange={(e) => setText(e.target.value)}
                      rows={10} placeholder="13800000001&#10;13800000002&#10;..." />
    </Modal>
  );
}

function Row({ label, children }: any) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
